import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, useHistory } from "react-router";
import type { ProductInfo } from "@b2bportal/chat-api";
import type { IChatProvider } from "@b2bportal/core-chat";
import {
  endChatSession,
  getHasActiveChatSession,
  getIsMinimized,
  minimizeChatWindow,
  toggleChatWindow,
} from "@b2bportal/core-self-serve";
import { ChatComponents } from "@hopper-b2b/chat";
import { useI18nContext } from "@hopper-b2b/i18n";
import { IconComponent, IconName } from "@hopper-b2b/ui";
import { useDeviceTypes } from "@hopper-b2b/utilities";
import { Box, Fab, Typography } from "@material-ui/core";
import clsx, { type ClassValue } from "clsx";
import type { Location } from "history";

import { useChatStyles } from "../../hooks";
import { defaultChatProvider } from "../../types/ChatProvider";
import { getBaseTripDetailsPath, resolvePaths } from "../../utils";
import { ChatWindow } from "../ChatWindow";

import defaultStyles from "./ChatFab.module.scss";

export interface IChatFabProps {
  /**
   * If set to true, it will display hideChatFabIcon inside Fab and hide it upon clicking
   * This only works when fabVariant is extended
   */
  allowHideFab?: boolean;
  chatProvider?: IChatProvider;
  className?: ClassValue;
  copyConversationIdEnabled?: boolean;
  getTripDetailsPath?: (productInfo: ProductInfo) => string;
  hideChatFabIcon?: string;
  /**
   * URL path fragments on which to **hide the ChatFab**.
   * Will be resolved to have a leading slash and no trailing slash
   * per react-router standards
   *
   * To err on the side of caution, a path here will take priority
   * if also included in `showPaths`
   */
  hidePaths?: string[];
  showChatFabIcon?: string;
  /**
   * URL path fragments on which to **show the ChatFab**.
   * Will be resolved to have a leading slash and no trailing slash
   * per react-router standards
   */
  showPaths?: string[];
  onClick?: () => void;
  onDismiss?: () => void;
}

const ChatFab: React.FC<IChatFabProps> = (props) => {
  const {
    allowHideFab = false,
    chatProvider = defaultChatProvider,
    className,
    copyConversationIdEnabled = false,
    getTripDetailsPath = getBaseTripDetailsPath,
    hideChatFabIcon,
    hidePaths = [],
    showChatFabIcon,
    showPaths = [],
    onClick,
    onDismiss,
  } = props;
  const { matchesMobile } = useDeviceTypes();
  const dispatch = useDispatch();
  const history = useHistory();
  const { brand, t } = useI18nContext();
  const hasActiveChatSession = useSelector(getHasActiveChatSession);
  const isMinimized = useSelector(getIsMinimized);
  const [showFab, setShowFab] = useState(false);
  const [resolvedHidePaths] = useState(resolvePaths(hidePaths));
  const [resolvedShowPaths] = useState(resolvePaths(showPaths));
  const fabVariant = brand.contactSupport.aiChat?.variant ?? "extended";
  const styles = useChatStyles(ChatComponents.ChatFab, defaultStyles);
  const closeChat = () => {
    dispatch(endChatSession());
  };

  const getHideFabIcon = () => {
    const className = clsx("fab-icon", styles.fabIcon);

    if (hideChatFabIcon) {
      return <img alt="Hide Chat" src={hideChatFabIcon} />;
    }

    return (
      <IconComponent className={className} name={IconName.RoundArrowDown} />
    );
  };

  const getShowFabIcon = () => {
    const className = clsx("fab-icon", styles.fabIcon);

    if (showChatFabIcon) {
      return <img alt="Show Chat" src={showChatFabIcon} />;
    }

    return <IconComponent className={className} name={IconName.SupportChat} />;
  };

  const handleOnClick = () => {
    onClick?.();
    dispatch(toggleChatWindow());
  };

  const handleDismiss = (e: React.MouseEvent) => {
    onDismiss?.();
    e.stopPropagation();
    setShowFab(false);
  };

  const minimizeChat = useCallback(() => {
    dispatch(minimizeChatWindow());
  }, [dispatch]);

  const onLocationChange = useCallback(
    (location?: Location) => {
      const { pathname } = location || window.location;
      const hideMatch = matchPath(pathname, resolvedHidePaths);
      const showMatch = matchPath(pathname, resolvedShowPaths);
      const shouldShowFab = Boolean(!hideMatch && showMatch);

      if (shouldShowFab) {
        // don't auto-open chat window as user navigates
        setShowFab(true);
      } else {
        minimizeChat();
        setShowFab(false);
      }
    },
    [minimizeChat, resolvedHidePaths, resolvedShowPaths]
  );

  /**
   * On first load, check if FAB should show or hide based on current path
   */
  useEffect(() => {
    onLocationChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * @description On init, add an event listener to route changes.
   * When the route changes, check to see if it's in the paths on
   * which to hide the chat button.
   */
  useEffect(() => {
    const unlisten = history.listen(onLocationChange);

    return () => {
      unlisten();
    };
  }, [history, onLocationChange]);

  return (
    <Box
      className={clsx(
        className,
        "chat-fab-container",
        {
          [styles.mobileOpen]: matchesMobile && !isMinimized,
          [styles.open]: !isMinimized,
        },
        styles.chatFabContainer
      )}
    >
      {hasActiveChatSession && (
        <ChatWindow
          chatProvider={chatProvider}
          closeChat={closeChat}
          copyConversationIdEnabled={copyConversationIdEnabled}
          minimizeChat={minimizeChat}
          mobile={matchesMobile}
          open={!isMinimized}
          getTripDetailsPath={getTripDetailsPath}
        />
      )}
      <Fab
        aria-label="help"
        className={clsx(
          "fab-button",
          {
            [styles.showFab]: showFab,
          },
          styles.fabButton
        )}
        classes={{ label: clsx("fab-label", styles.fabLabel) }}
        onClick={handleOnClick}
        variant={fabVariant}
      >
        {isMinimized ? getShowFabIcon() : getHideFabIcon()}
        {fabVariant === "extended" && (
          <Typography
            className={clsx("fab-title", styles.fabTitle)}
            variant="body1"
          >
            {t("chat.fabTitle")}
          </Typography>
        )}
        {fabVariant === "extended" && allowHideFab && (
          <img
            onClick={handleDismiss}
            className={clsx(styles.closeIcon, { [styles.hideFab]: !showFab })}
            alt="Hide Chat"
            src={hideChatFabIcon}
          />
        )}
      </Fab>
    </Box>
  );
};

export default ChatFab;
